// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React from "react";
import i18n from "i18next";
import { navigate } from "@reach/router";
import {
  BranchListDto,
  NewsThemeInfoResponseDTO
} from "../../../../api/backend-openapi";
import { alignRight, NEWS_THEME_STATUS } from "../../../../utils/Constant";
import DateFormat from "../../../../utils/DateFormat";
import TableButton from "../../../components/TableButton";
import {
  DeleteOutlined,
  EyeOutlined,
  MoreOutlined,
  SendOutlined,
  StopOutlined,
  UpOutlined,
  DownOutlined
} from "@ant-design/icons/lib";
import { Button, Popover, Tag } from "antd";
import { showConfirm } from "../../../components/ConfirmModal";
import {
  requestDecreaseOrderNewsTheme,
  requestDeleteNewsTheme,
  requestIncreaseOrderNewsTheme,
  requestWorkflowNewsTheme
} from "../../../../state/newsTheme/NewsThemeEffects";
import NewsThemeStatusTag from "../details/NewsThemeStatusTag";
import { tooltipRender } from "../../../components/CustomTooltip";

const popoverContent = (
  record: NewsThemeInfoResponseDTO,
  canValidate: boolean,
  canDelete: boolean,
  newsThemeList: NewsThemeInfoResponseDTO[] | null
) => (
  <div className="d-inline-flex">
    <TableButton
      icon={<EyeOutlined />}
      tooltipTitle={i18n.t("news_themes.table.tooltip.view")}
      onClick={() => {
        navigate(`/newsThemes/${record.id}`);
      }}
      buttonClassName="btn-primary mr-1 btn-list"
    />
    {record.publicationDate === null && canValidate && (
      <TableButton
        icon={<SendOutlined />}
        buttonClassName="btn-success mr-1 btn-list"
        onClick={() =>
          showConfirm(
            i18n.t("news_themes.table.confirmation.publish", {
              rubrique: record.name
            }),
            () => {
              requestWorkflowNewsTheme({
                id: record.id,
                dto: {
                  published: true
                }
              });
            }
          )
        }
        tooltipTitle={i18n.t("news_themes.table.tooltip.publish")}
      />
    )}
    {record.publicationDate && canValidate && !record.isDefault && (
      <TableButton
        icon={<StopOutlined />}
        buttonClassName="btn-secondary mr-1 btn-list"
        onClick={() =>
          showConfirm(
            i18n.t("news_themes.table.confirmation.unpublish", {
              rubrique: record.name
            }),
            () => {
              requestWorkflowNewsTheme({
                id: record.id,
                dto: {
                  published: false
                }
              });
            }
          )
        }
        tooltipTitle={i18n.t("news_themes.table.tooltip.unpublish")}
      />
    )}
    {record.publicationDate === null && canDelete && !record.isDefault && (
      <TableButton
        icon={<DeleteOutlined />}
        buttonClassName="btn-danger mr-1 btn-list"
        onClick={() =>
          showConfirm(
            i18n.t("news_themes.table.confirmation.delete", {
              rubrique: record.name
            }),
            () => {
              requestDeleteNewsTheme(record);
            }
          )
        }
        tooltipTitle={i18n.t("news_themes.table.tooltip.delete")}
      />
    )}
    {canValidate && newsThemeList && record.order > 0 && (
      <TableButton
        icon={<UpOutlined />}
        buttonClassName="btn-order mr-1 btn-list"
        onClick={() =>
          showConfirm(
            i18n.t("news_themes.table.confirmation.upNewsTheme", {
              rubrique: record.name
            }),
            () => {
              requestIncreaseOrderNewsTheme(record);
            }
          )
        }
        tooltipTitle={i18n.t("news_themes.table.tooltip.upNewsTheme")}
      />
    )}
    {canValidate &&
      !!newsThemeList &&
      record.order <
        newsThemeList
          ?.filter(item => item.branch === record.branch)
          ?.reduce(
            (acc, value) => (value.order > acc ? value.order : acc),
            0
          ) && (
        <TableButton
          icon={<DownOutlined />}
          buttonClassName="btn-order mr-1 btn-list"
          onClick={() =>
            showConfirm(
              i18n.t("news_themes.table.confirmation.downNewsTheme", {
                rubrique: record.name
              }),
              () => {
                requestDecreaseOrderNewsTheme(record);
              }
            )
          }
          tooltipTitle={i18n.t("news_themes.table.tooltip.downNewsTheme")}
        />
      )}
  </div>
);

export const columns = (
  getColumnSearchProps: any,
  canValidate: boolean,
  canDelete: boolean,
  newsThemeList: NewsThemeInfoResponseDTO[] | null,
  branchList: BranchListDto
) => {
  return [
    {
      title: i18n.t("news_themes.table.name"),
      key: "name",
      ellipsis: true,
      render: tooltipRender,
      ...getColumnSearchProps()
    },
    {
      title: i18n.t("news_themes.table.branch"),
      key: "branch",
      dataIndex: "branch",
      ellipsis: false,
      render: (branch?: string) => {
        if (branch) {
          return branchList[branch]?.label || "";
        }
        return "";
      },
      filters: Object.keys(branchList).map(key => {
        return {
          text: branchList[key].label,
          value: key
        };
      }),
      filterMultiple: false,
      defaultFilteredValue: undefined,
      onFilter: (value: string, record: NewsThemeInfoResponseDTO) => {
        return value === undefined || value === record.branch;
      }
    },
    {
      title: i18n.t("news_themes.table.order"),
      key: "order",
      dataIndex: "order",
      render: (order: number) =>
        order !== null && order !== undefined ? order + 1 : "",
      ellipsis: true,
      width: "5%"
    },
    {
      title: i18n.t("news_themes.table.status"),
      key: "status",
      ellipsis: true,
      filters: [
        { text: NEWS_THEME_STATUS.PUBLISHED.label, value: "published" },
        { text: NEWS_THEME_STATUS.UNPUBLISHED.label, value: "unpublished" }
      ],
      defaultFilteredValue: undefined,
      onFilter: (value: string, record: NewsThemeInfoResponseDTO) => {
        if (value === "published") {
          return record.publicationDate;
        } else if (value === "unpublished") {
          return !record.publicationDate;
        } else {
          return true;
        }
      },
      render: (record: NewsThemeInfoResponseDTO) => {
        return (
          record && (
            <NewsThemeStatusTag publicationDate={record.publicationDate} />
          )
        );
      }
    },
    {
      title: i18n.t("news_themes.table.defaultNewsTheme"),
      key: "defaultNewsTheme",
      ellipsis: false,
      width: "9%",
      render: (record: NewsThemeInfoResponseDTO) => {
        return record?.isDefault ? (
          <Tag color={"#778899"}>{i18n.t("news_theme.defaultNewsTheme")}</Tag>
        ) : null;
      }
    },
    {
      title: i18n.t("news_themes.table.publication_date"),
      key: "publicationDate",
      ellipsis: true,
      render: (record: NewsThemeInfoResponseDTO) => {
        return (
          <span>
            {record.publicationDate
              ? DateFormat.standardDate(record.publicationDate)
              : ""}
          </span>
        );
      }
    },
    {
      title: i18n.t("news_themes.table.creation_date"),
      key: "creationDate",
      dataIndex: "creationDate",
      ellipsis: true,
      render: (record: Date) => {
        return <span>{DateFormat.standardDate(record)}</span>;
      }
    },
    {
      title: i18n.t("news_themes.table.modification_date"),
      key: "modificationDate",
      dataIndex: "modificationDate",
      ellipsis: true,
      render: (record: Date) => {
        return <span>{DateFormat.standardDate(record)}</span>;
      }
    },
    {
      title: i18n.t("news_themes.table.action"),
      key: "action",
      fixed: alignRight,
      width: 90,
      onCell: () => {
        return {
          onClick: (e: any) => e.stopPropagation()
        };
      },
      render: (text: string, record: NewsThemeInfoResponseDTO) => (
        <Popover
          content={popoverContent(
            record,
            canValidate,
            canDelete,
            newsThemeList
          )}
          placement="bottomRight"
          trigger="hover"
        >
          <Button
            icon={<MoreOutlined />}
            type="text"
            size="large"
            className="btn-list"
          />
        </Popover>
      )
    }
  ];
};
